import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { redirectToLogin } from "../utils/auth";
import {
  ChevronIcon,
  DownloadIcon,
  CalendarIcon,
  EmptyStateIcon,
} from "../Components/Icons/Icons";
import { LatestTickets } from "../Components/Home/LatestTickets";
import { useUser } from "../context/UserContext";

import * as XLSX from "xlsx";

// API Base URL from environment
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined relative date ranges
const PRESET_RANGES = {
  "1d": { label: "Last 24 hours", days: 1 },
  // "3d": { label: "Last 3 days", days: 3 },
  "7d": { label: "Last 7 days", days: 7 },
  // "14d": { label: "Last 14 days", days: 14 },
  "30d": { label: "Last 30 days", days: 30 },
};

const DEFAULT_RANGE = "7d";

// Helper to format date as YYYY-MM-DD (local timezone)
const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper to format date for display (e.g., "Jan 15")
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Validate custom date range
const isValidCustomRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return start <= end && end <= today;
};

// Safe percentage calculation (handles division by zero)
const safePercentage = (numerator, denominator) => {
  if (!denominator || denominator === 0) return 0;
  return (numerator / denominator) * 100;
};

// Format hours for display
const formatHours = (hours) => {
  if (hours === null || hours === undefined || isNaN(hours)) return "—";
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  }
  return `${hours.toFixed(1)} hrs`;
};

// Format percentage for display
const formatPercentage = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return `${Math.round(value)}%`;
};

// Shimmer animation styles (injected once)
const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  .animate-shimmer {
    animation: shimmer 1.5s ease-in-out infinite;
  }
`;

// Skeleton Card component with shimmer animation
const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm border border-violet-100 p-6">
    {/* Title skeleton - matches "text-sm" height */}
    <div
      className="h-4 w-28 mb-3 rounded animate-shimmer"
      style={{
        background:
          "linear-gradient(90deg, #ede9fe 25%, #f5f3ff 50%, #ede9fe 75%)",
        backgroundSize: "200% 100%",
      }}
    />
    {/* Value skeleton - matches "text-3xl" height */}
    <div
      className="h-9 w-24 rounded animate-shimmer"
      style={{
        background:
          "linear-gradient(90deg, #ede9fe 25%, #f5f3ff 50%, #ede9fe 75%)",
        backgroundSize: "200% 100%",
      }}
    />
  </div>
);

// Loading skeleton grid (6 cards to match actual layout)
const SkeletonGrid = () => (
  <>
    <style>{shimmerStyles}</style>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {[...Array(6)].map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  </>
);

export const Analytics = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInputs, setShowCustomInputs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Analytics data from API
  const [analyticsData, setAnalyticsData] = useState({
    totalCount: 0,
    SLABreachedCount: 0,
    avgFirstResponseHours: 0,
    avgResolutionTimeHours: 0,
    reopenedTicketsCount: 0,
  });

  const [tickerTickets, setTickerTickets] = useState([]);
  const [tickerPriorities, setTickerPriorities] = useState([]);
  const [tickerStatuses, setTickerStatuses] = useState([]);

  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");

  const { user } = useUser();
  // Internal users only; partners get redirected below.
  const authorized = user.loaded && user.isPartner === false;

  // Partners aren't allowed on Analytics — bounce them to the dashboard.
  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }
    if (user.loaded && user.isPartner) {
      navigate("/Dashboard", { replace: true });
    }
  }, [authToken, navigate, user.loaded, user.isPartner]);

  // Ticker data — same standard sources the dashboard uses.
  useEffect(() => {
    if (!authorized || !authToken) return;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };
    const get = (url) =>
      fetch(url, { headers }).then((r) => {
        if (r.status === 401) {
          redirectToLogin();
          return null;
        }
        return r.json();
      });

    Promise.all([
      get(`${API_BASE_URL}/API/V2/Tickets`),            // tickets
      get(`${API_BASE_URL}/API/V2/config/priorities`),   // priorities
      get(`${API_BASE_URL}/API/V2/config/status`),       // statuses
    ])
      .then(([ticketsData, prioritiesData, statusData]) => {
        if (!ticketsData) return;
        setTickerTickets(ticketsData || []);
        setTickerPriorities(prioritiesData || []);
        setTickerStatuses(statusData || []);
      })
      .catch(() => {});
  }, [authorized, authToken]);

  // Parse URL parameters
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const urlRange = queryParams.get("range");
  const urlStart = queryParams.get("start");
  const urlEnd = queryParams.get("end");

  // Local state for custom date picker
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Calculate date range based on preset
  const calculateDateRange = (range) => {
    const today = new Date();
    const endDate = formatDateForAPI(today);

    if (range === "1d") {
      return { start: endDate, end: endDate };
    }

    const daysBack = PRESET_RANGES[range]?.days || 7;
    const startDateObj = new Date(today);
    startDateObj.setDate(today.getDate() - (daysBack - 1));
    return { start: formatDateForAPI(startDateObj), end: endDate };
  };

  // Determine effective range from URL
  const effectiveRange = useMemo(() => {
    if (urlRange === "custom" && isValidCustomRange(urlStart, urlEnd)) {
      return { type: "custom", range: "custom", start: urlStart, end: urlEnd };
    }
    if (urlRange && PRESET_RANGES[urlRange]) {
      const { start, end } = calculateDateRange(urlRange);
      return { type: "preset", range: urlRange, start, end };
    }
    // Default
    const { start, end } = calculateDateRange(DEFAULT_RANGE);
    return { type: "preset", range: DEFAULT_RANGE, start, end };
  }, [urlRange, urlStart, urlEnd]);

  // Fetch analytics data when date range changes
  useEffect(() => {
    // Redirect if no range in URL
    if (!urlRange) {
      const newParams = new URLSearchParams(queryParams);
      newParams.set("range", DEFAULT_RANGE);
      navigate(`${location.pathname}?${newParams.toString()}`, {
        replace: true,
      });
      return;
    }

    // For preset ranges, ensure start/end are in URL
    if (urlRange !== "custom" && PRESET_RANGES[urlRange]) {
      const { start, end } = calculateDateRange(urlRange);
      if (urlStart !== start || urlEnd !== end) {
        const newParams = new URLSearchParams(queryParams);
        newParams.set("start", start);
        newParams.set("end", end);
        navigate(`${location.pathname}?${newParams.toString()}`, {
          replace: true,
        });
        return;
      }
    }

    // For custom range, ensure both dates exist
    if (urlRange === "custom" && (!urlStart || !urlEnd)) {
      return;
    }

    // Fetch data from API
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/API/V2/analytics/sla-summary?start=${effectiveRange.start}&end=${effectiveRange.end}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        setAnalyticsData(response.data);
      } catch (err) {
        console.error("Analytics API Error:", err);
        setError("Failed to load analytics data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [
    urlRange,
    urlStart,
    urlEnd,
    effectiveRange.start,
    effectiveRange.end,
    queryParams,
    navigate,
    location.pathname,
  ]);

  // Sync custom date inputs from URL
  useEffect(() => {
    if (effectiveRange.type === "custom") {
      setCustomStartDate(effectiveRange.start);
      setCustomEndDate(effectiveRange.end);
    }
  }, [effectiveRange]);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustomInputs(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setShowCustomInputs(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Handle preset range selection
  const handlePresetSelect = (rangeKey) => {
    const { start, end } = calculateDateRange(rangeKey);
    const newParams = new URLSearchParams();
    newParams.set("range", rangeKey);
    newParams.set("start", start);
    newParams.set("end", end);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
    setIsOpen(false);
    setShowCustomInputs(false);
  };

  // Show custom date inputs
  const handleCustomClick = () => {
    if (effectiveRange.type === "custom") {
      setCustomStartDate(effectiveRange.start);
      setCustomEndDate(effectiveRange.end);
    } else {
      const today = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      setCustomStartDate(formatDateForAPI(weekAgo));
      setCustomEndDate(formatDateForAPI(today));
    }
    setShowCustomInputs(true);
  };

  // Apply custom date range
  const applyCustomRange = () => {
    if (isValidCustomRange(customStartDate, customEndDate)) {
      const newParams = new URLSearchParams();
      newParams.set("range", "custom");
      newParams.set("start", customStartDate);
      newParams.set("end", customEndDate);
      navigate(`${location.pathname}?${newParams.toString()}`, {
        replace: true,
      });
      setIsOpen(false);
      setShowCustomInputs(false);
    }
  };

  // Get display label for current selection
  const getDisplayLabel = () => {
    if (effectiveRange.type === "custom") {
      return `${formatDisplayDate(effectiveRange.start)} – ${formatDisplayDate(
        effectiveRange.end
      )}`;
    }
    return PRESET_RANGES[effectiveRange.range]?.label || "Select range";
  };

  // Calculate derived metrics
  const {
    totalCount,
    SLABreachedCount,
    avgFirstResponseHours,
    avgResolutionTimeHours,
    reopenedTicketsCount,
  } = analyticsData;

  const slaCompliancePercent = safePercentage(
    totalCount - SLABreachedCount,
    totalCount
  );
  const slaBreachPercent = safePercentage(SLABreachedCount, totalCount);
  const reopenRatePercent = safePercentage(reopenedTicketsCount, totalCount);

async function downloadExcel() {
  try {
    // Backend returns flattened JSON rows (joins history/teams server-side);
    // the workbook itself is built here on the client.
    const response = await axios.get(
      `${API_BASE_URL}/API/V2/analytics/resolved-tickets?start=${effectiveRange.start}&end=${effectiveRange.end}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    const data = response.data;

    if (!Array.isArray(data) || data.length === 0) {
      alert("No resolved tickets found for the selected range.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resolved Tickets");
    XLSX.writeFile(
      workbook,
      `resolved-tickets_${effectiveRange.start}_to_${effectiveRange.end}.xlsx`
    );
  } catch (error) {
    console.error("Excel download failed:", error);
    alert("Failed to download the report. Please try again.");
  }
}

  if (!authorized) return null;

  return (
    <div className="p-2 rounded-3xl mx-6 mt-4">
      {/* <LatestTickets
        tickets={tickerTickets}
        username={user.username}
        access={user.isPMO ? 3 : 1}
        priorities={tickerPriorities}
        statuses={tickerStatuses}
      /> */}
      <div className="flex gap-3 m-3 mb-0 mr-2 items-center">
        <p className="mr-auto text-2xl font-black tracking-tight leading-snug">
          SLA Dashboard
        </p>

        {/* Time Range Popover */}
        <div className="relative" ref={dropdownRef}>
          {/* Trigger Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`
              inline-flex items-center gap-2 px-3 py-1.5 text-sm
              bg-white border border-violet-200 rounded-lg
              text-violet-700 hover:border-violet-400 hover:shadow-sm
              transition-colors duration-150 ease-out
              outline-none focus:outline-none focus:border-violet-500
              ${isOpen ? "border-violet-500 shadow-sm" : ""}
            `}
          >
            <CalendarIcon size={14} color="#a78bfa" />
            <span className="font-medium">{getDisplayLabel()}</span>
            <ChevronIcon size={14} color="#a78bfa" isOpen={isOpen} />
          </button>

          {/* Popover Panel */}
          <div
            className={`
              absolute right-0 mt-1.5 z-50
              bg-white rounded-lg shadow-lg border border-violet-100
              transform origin-top-right
              transition-all duration-150 ease-out
              ${
                isOpen
                  ? "opacity-100 scale-100 translate-y-0"
                  : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
              }
            `}
            style={{ minWidth: "200px" }}
          >
            {/* Preset Options */}
            <div className="p-1">
              {Object.entries(PRESET_RANGES).map(([key, { label }]) => (
                <button
                  key={key}
                  onClick={() => handlePresetSelect(key)}
                  className={`
                    w-full text-left px-3 py-1.5 text-sm rounded-md
                    transition-colors duration-100
                    ${
                      effectiveRange.range === key
                        ? "bg-violet-100 text-violet-700 font-medium"
                        : "text-gray-600 hover:bg-violet-50 hover:text-violet-700"
                    }
                  `}
                >
                  {label}
                </button>
              ))}

              {/* Divider */}
              <div className="my-1 border-t border-violet-100" />

              {/* Custom Range Option */}
              <button
                onClick={handleCustomClick}
                className={`
                  w-full text-left px-3 py-1.5 text-sm rounded-md
                  transition-colors duration-100
                  ${
                    effectiveRange.type === "custom" || showCustomInputs
                      ? "bg-violet-100 text-violet-700 font-medium"
                      : "text-gray-600 hover:bg-violet-50 hover:text-violet-700"
                  }
                `}
              >
                Custom range
              </button>
            </div>

            {/* Custom Date Inputs */}
            <div
              className={`
                overflow-hidden transition-all duration-200 ease-out
                ${
                  showCustomInputs
                    ? "max-h-40 opacity-100"
                    : "max-h-0 opacity-0"
                }
              `}
            >
              <div className="px-3 pb-3 pt-1 border-t border-violet-100 mt-1">
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="date"
                    className="flex-1 px-2 py-1 text-xs border border-violet-200 rounded-md
                      text-violet-700 bg-white
                      focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400
                      transition-colors duration-100"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || formatDateForAPI(new Date())}
                  />
                  <span className="text-violet-300 text-xs">–</span>
                  <input
                    type="date"
                    className="flex-1 px-2 py-1 text-xs border border-violet-200 rounded-md
                      text-violet-700 bg-white
                      focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400
                      transition-colors duration-100"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                    max={formatDateForAPI(new Date())}
                  />
                </div>
                <button
                  className="w-full mt-2 px-3 py-1.5 text-xs font-medium
                    bg-violet-600 text-white rounded-md
                    hover:bg-violet-700 active:bg-violet-800
                    disabled:opacity-40 disabled:cursor-not-allowed
                    transition-colors duration-100
                    focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1"
                  onClick={applyCustomRange}
                  disabled={!isValidCustomRange(customStartDate, customEndDate)}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Download Button (visual only - no backend yet) */}
        <button
          onClick={() => {downloadExcel()}}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm
            bg-white border border-violet-200 rounded-lg
            text-violet-700 hover:border-violet-400 hover:shadow-sm
            transition-colors duration-150 ease-out
            outline-none focus:outline-none focus:border-violet-500"
        >
          <DownloadIcon size={16} color="#8b5cf6" />
          <span className="font-medium">Download</span>
        </button>
      </div>

      <div className="p-2 mt-2">
        {loading ? (
          <SkeletonGrid />
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            <p className="text-lg">{error}</p>
            <p className="text-sm mt-2">
              Please check your connection and try again.
            </p>
          </div>
        ) : totalCount === 0 ? (
          <div className="w-full h-72 flex flex-col items-center justify-center">
            <div className="mb-3">
              <EmptyStateIcon size={64} />
            </div>
            <p className="font-semibold text-lg text-violet-400">
              No tickets found
            </p>
            <p className="text-sm text-violet-300 mt-1">
              Try selecting a different date range
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card title="Total Tickets" value={totalCount} />
            <Card
              title="Avg. Response Time"
              value={formatHours(avgFirstResponseHours)}
            />
            <Card
              title="Avg. Resolution Time"
              value={formatHours(avgResolutionTimeHours)}
            />
            <Card
              title="SLA Compliance"
              value={formatPercentage(slaCompliancePercent)}
            />
            <Card
              title="SLA Breaches"
              value={formatPercentage(slaBreachPercent)}
            />
            <Card
              title="Reopen Rate"
              value={formatPercentage(reopenRatePercent)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

function Card({ title, value }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-violet-100 p-6 hover:shadow-md transition">
      <p className="text-sm text-violet-500 mb-2">{title}</p>
      <p className="text-3xl font-semibold text-violet-800">{value}</p>
    </div>
  );
}
